import {
  Component,
  OnInit,
  OnDestroy,
  computed,
  ViewEncapsulation,
} from "@angular/core";

import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import {
  GlobalAlertService,
  GlobalAlertConfig,
} from "../../services/global-alert.service";
import { Subscription } from "rxjs";

@Component({
  selector: "tp-global-alert",
  imports: [MatButtonModule, MatIconModule],
  templateUrl: "./global-alert.component.html",
  styleUrls: ["./global-alert.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class GlobalAlertComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;

  // Reactive signals from service
  isVisible = this.globalAlertService.isVisible;
  currentAlert = this.globalAlertService.currentAlert;

  // Computed properties for easier template usage
  alertType = computed(() => this.currentAlert()?.type || "info");
  hasTitle = computed(() => !!this.currentAlert()?.title);
  showCancel = computed(() => this.currentAlert()?.showCancel ?? true);

  constructor(private globalAlertService: GlobalAlertService) {}

  ngOnInit(): void {
    // Component is reactive via signals, no additional setup needed
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onConfirm(): void {
    this.globalAlertService.confirm();
  }

  onCancel(): void {
    this.globalAlertService.cancel();
  }

  onBackdropClick(event: Event): void {
    // Prevent closing when clicking inside the alert content
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  /**
   * Get the appropriate SVG icon based on alert type
   */
  getIconSvg(type: string): string {
    switch (type) {
      case "warning":
        return `
          <svg viewBox="0 0 24 24" class="alert-icon" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- AI Brain/Network Icon for intelligent analysis -->
            <!-- Central core -->
            <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.9"/>
            
            <!-- Connection lines -->
            <line x1="12" y1="12" x2="6" y2="6" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="18" y2="6" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="6" y2="18" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="18" y2="18" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="12" y2="4" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="4" y2="12" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            <line x1="12" y1="12" x2="12" y2="20" stroke="currentColor" stroke-width="1" opacity="0.6"/>
            
            <!-- Network nodes -->
            <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.7" class="ai-node node-1"/>
            <circle cx="18" cy="6" r="1.5" fill="currentColor" opacity="0.7" class="ai-node node-2"/>
            <circle cx="6" cy="18" r="1.5" fill="currentColor" opacity="0.7" class="ai-node node-3"/>
            <circle cx="18" cy="18" r="1.5" fill="currentColor" opacity="0.7" class="ai-node node-4"/>
            <circle cx="12" cy="4" r="1" fill="currentColor" opacity="0.5" class="ai-node node-5"/>
            <circle cx="4" cy="12" r="1" fill="currentColor" opacity="0.5" class="ai-node node-6"/>
            <circle cx="20" cy="12" r="1" fill="currentColor" opacity="0.5" class="ai-node node-7"/>
            <circle cx="12" cy="20" r="1" fill="currentColor" opacity="0.5" class="ai-node node-8"/>
            
            <!-- Warning overlay -->
            <polygon points="12,8 10,16 14,16" fill="currentColor" opacity="0.9"/>
            <circle cx="12" cy="18" r="1" fill="currentColor"/>
          </svg>
        `;
      case "error":
        return `
          <svg viewBox="0 0 24 24" class="alert-icon" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
            <path d="15 9l-6 6m0-6l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `;
      case "success":
        return `
          <svg viewBox="0 0 24 24" class="alert-icon" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
            <path d="m9 12 2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      case "info":
      default:
        return `
          <svg viewBox="0 0 24 24" class="alert-icon" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
            <path d="M12 16v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <circle cx="12" cy="8" r="1" fill="currentColor"/>
          </svg>
        `;
    }
  }
}
